import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import SignatureCanvas from 'react-signature-canvas'

import * as Constants from '@/_config/app'
import { trans } from '@/_providers/Translation'
import { VERIFY_TOKEN, publicSignEndpoint, pdfUrl } from '../config'

import '@/assets/scss/pages/contract-sign.scss'

//images
import brandImage from '@/assets/img/login/brand.svg'
import loader from '@/assets/img/loader.svg'

// Страницата е публична - клиентът идва по линк от е-пощата и няма профил.
// Затова не минаваме през @/_helpers/Api: той закача Bearer токен и при 401
// изхвърля към екрана за вход, което тук би било напълно погрешно.
const PublicApi = axios.create({
    baseURL: Constants.App.api
})

const CANVAS_HEIGHT = 260

// Едно случайно докосване оставя няколко точки - искаме истински подпис.
const MIN_SIGNATURE_POINTS = 8

const countPoints = strokes => {
    if (!Array.isArray(strokes)) {
        return 0
    }

    return strokes.reduce((total, stroke) => {
        // signature_pad връща или { points: [...] }, или самия масив с точки.
        const points = Array.isArray(stroke) ? stroke : (stroke?.points || [])

        return total + points.length
    }, 0)
}

function SignContract() {

    const params = useParams()

    const token = (params.token || '').trim()

    const padRef = useRef(null)
    const canvasWrapRef = useRef(null)
    const canvasSizeRef = useRef({ width: 0, ratio: 0 })

    const [state, setState] = useState({
        // loading | ready | signed | invalid
        status: 'loading',
        contract: null,
        pdf: '',
    })

    const [pad, setPad] = useState({
        drawn: false,
        saving: false,
        error: '',
    })

    // Линкът е валиден само ако носи токен и този токен още се приема.
    useEffect(() => {

        if (!token) {
            setState(prev => ({ ...prev, status: 'invalid' }))
            return
        }

        let cancelled = false

        PublicApi.post(VERIFY_TOKEN, { token: token })
            .then(res => {
                if (cancelled) {
                    return
                }

                const data = res.data || {}

                // Някои маршрути отказват с код, други връщат 200 с признак в
                // тялото - и двете означават негоден линк.
                if (data.valid === false || data.success === false) {
                    setState(prev => ({ ...prev, status: 'invalid' }))
                    return
                }

                // Договорът може да дойде и подгънат в тялото на отговора.
                const contract = data.contract || data.data || data
                const signed = Boolean(contract.signed_by_party_at || contract.signed || contract.is_signed)

                setState({
                    status: signed ? 'signed' : 'ready',
                    contract: contract,
                    pdf: signed ? pdfUrl(contract) : '',
                })
            }).catch(() => {
                if (!cancelled) {
                    setState(prev => ({ ...prev, status: 'invalid' }))
                }
            })

        return () => {
            cancelled = true
        }
    }, [token])

    // Платното се разпъва по контейнера и се преоразмерява при завъртане на
    // телефона. Преоразмеряването изтрива рисунката, затова я пренасяме.
    const fitCanvas = useCallback(() => {

        const signaturePad = padRef.current
        const wrap = canvasWrapRef.current

        if (!signaturePad || !wrap) {
            return
        }

        const ratio = Math.max(window.devicePixelRatio || 1, 1)
        const width = wrap.clientWidth

        // Скрит таб или още неподреден изглед - изчакваме реалния размер.
        if (!width) {
            return
        }

        // Преоразмеряването изчиства платното, затова го правим само когато
        // размерът наистина се е сменил.
        if (width === canvasSizeRef.current.width && ratio === canvasSizeRef.current.ratio) {
            return
        }

        canvasSizeRef.current = { width: width, ratio: ratio }

        const canvas = signaturePad.getCanvas()
        const strokes = signaturePad.toData()

        canvas.width = width * ratio
        canvas.height = CANVAS_HEIGHT * ratio
        canvas.style.width = width + 'px'
        canvas.style.height = CANVAS_HEIGHT + 'px'
        canvas.getContext('2d').scale(ratio, ratio)

        signaturePad.clear()

        if (strokes && strokes.length) {
            signaturePad.fromData(strokes)
        }
    }, [])

    useEffect(() => {

        if (state.status !== 'ready') {
            return
        }

        fitCanvas()

        // Платното може да се появи, докато контейнерът още е с нулева
        // широчина. Наблюдателят го оразмерява в мига, в който получи размер -
        // само window.resize не би се задействал в този случай.
        let observer = null

        if (typeof ResizeObserver !== 'undefined' && canvasWrapRef.current) {
            observer = new ResizeObserver(() => fitCanvas())
            observer.observe(canvasWrapRef.current)
        }

        window.addEventListener('resize', fitCanvas)
        window.addEventListener('orientationchange', fitCanvas)

        return () => {
            observer && observer.disconnect()
            window.removeEventListener('resize', fitCanvas)
            window.removeEventListener('orientationchange', fitCanvas)
        }
    }, [state.status, fitCanvas])

    const handleBegin = () => {
        setPad(prev => ({
            ...prev,
            error: ''
        }))
    }

    const handleEnd = () => {
        setPad(prev => ({
            ...prev,
            drawn: true
        }))
    }

    // "Подпиши отново" - изчиства платното.
    const handleClear = () => {
        padRef.current && padRef.current.clear()

        setPad({
            drawn: false,
            saving: false,
            error: '',
        })
    }

    // Отваряме готовия документ веднага. Изскачащите прозорци често се блокират
    // при отваряне след заявка, затова линкът остава видим и на екрана.
    const openPdf = url => {
        if (!url) {
            return
        }

        const opened = window.open(url, '_blank')

        if (!opened) {
            setPad(prev => ({
                ...prev,
                error: trans('random.popupBlocked')
            }))
        }
    }

    const handleSave = () => {

        const signaturePad = padRef.current

        if (!signaturePad || pad.saving) {
            return
        }

        if (signaturePad.isEmpty()) {
            setPad(prev => ({
                ...prev,
                error: trans('random.signatureEmpty')
            }))
            return
        }

        if (countPoints(signaturePad.toData()) < MIN_SIGNATURE_POINTS) {
            setPad(prev => ({
                ...prev,
                error: trans('random.signatureTooShort')
            }))
            return
        }

        setPad(prev => ({
            ...prev,
            saving: true,
            error: '',
        }))

        const signature = signaturePad.getTrimmedCanvas().toDataURL('image/png')

        PublicApi.post(publicSignEndpoint(token), {
            signature: signature,
        }).then(res => {

            const url = pdfUrl(res.data)

            setState(prev => ({
                ...prev,
                status: 'signed',
                pdf: url,
            }))

            setPad(prev => ({
                ...prev,
                saving: false,
            }))

            openPdf(url)

        }).catch(error => {

            const status = error.response?.status

            // Изтекъл или вече използван линк - няма смисъл да опитва пак.
            if (status === 404 || status === 410) {
                setState(prev => ({ ...prev, status: 'invalid' }))
                return
            }

            setPad(prev => ({
                ...prev,
                saving: false,
                error: trans('random.signSaveFailed'),
            }))
        })
    }

    return (
        <section id="contract-sign">
            <img src={brandImage} alt="brand" className="brand" />

            <div className="card">
                {state.status === 'loading' &&
                    <div className="state">
                        <img src={loader} alt="loading" />
                    </div>
                }

                {state.status === 'invalid' &&
                    <div className="state">
                        <h1>{trans('headings.signContract')}</h1>
                        <p className="error">
                            {trans('random.signLinkInvalid')}
                        </p>
                    </div>
                }

                {state.status === 'signed' &&
                    <div className="state">
                        <h1>{trans('headings.signContract')}</h1>
                        <p className="success">
                            {trans('random.signSaved')}
                        </p>
                        {state.pdf &&
                            <a
                                href={state.pdf}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="button button-link"
                                style={{
                                    textDecoration: 'underline',
                                }}
                            >
                                {trans('buttons.openPdf')}
                            </a>
                        }
                        {pad.error &&
                            <p className="hint">
                                {pad.error}
                            </p>
                        }
                    </div>
                }

                {state.status === 'ready' &&
                    <>
                        <div className="head">
                            <h1>{trans('headings.signContract')}</h1>
                            <p>{trans('subheadings.signContract')}</p>
                            {state.contract?.title &&
                                <b className="contract-title">
                                    {state.contract.title}
                                </b>
                            }
                        </div>

                        <div className="canvas-wrap" ref={canvasWrapRef}>
                            <SignatureCanvas
                                ref={padRef}
                                penColor="#262f5f"
                                clearOnResize={false}
                                onBegin={handleBegin}
                                onEnd={handleEnd}
                                canvasProps={{
                                    className: 'signature-canvas',
                                }}
                            />
                            {!pad.drawn &&
                                <span className="placeholder">
                                    {trans('random.signHere')}
                                </span>
                            }
                        </div>

                        {pad.error &&
                            <p className="error">
                                {pad.error}
                            </p>
                        }

                        <div className="actions">
                            {pad.saving
                                ?
                                <img src={loader} alt="loading" />
                                :
                                <>
                                    <button
                                        type="button"
                                        className="button"
                                        onClick={handleClear}
                                        disabled={!pad.drawn}
                                    >
                                        {trans('buttons.signAgain')}
                                    </button>
                                    <button
                                        type="button"
                                        className="button button-primary"
                                        onClick={handleSave}
                                        disabled={!pad.drawn}
                                    >
                                        {trans('buttons.saveSign')}
                                    </button>
                                </>
                            }
                        </div>
                    </>
                }
            </div>
        </section>
    )
}

export default SignContract
